import { getUnsyncedDrafts, markSynced } from './offlineStorage';
import { updateDraft } from './api';

const isDemoUser = () => {
  try {
    const stored = localStorage.getItem('draftflow_user');
    return stored ? JSON.parse(stored).isDemo === true : false;
  } catch { return false; }
};

let syncInProgress = false;
let syncStatus = 'idle'; // idle, syncing, synced, error

const listeners = new Set();

const notifyListeners = () => {
  listeners.forEach(fn => fn(syncStatus));
};

export const onSyncStatusChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const getSyncStatus = () => syncStatus;

export const syncNow = async () => {
  if (isDemoUser()) return { skipped: true, reason: 'demo' };
  if (syncInProgress) return;
  syncInProgress = true;
  syncStatus = 'syncing';
  notifyListeners();

  try {
    const unsynced = await getUnsyncedDrafts();
    if (unsynced.length === 0) {
      syncStatus = 'synced';
      notifyListeners();
      return;
    }

    for (const draft of unsynced) {
      try {
        await updateDraft(draft.draftId, draft.content, draft.word_count);
        await markSynced(draft.draftId);
      } catch (err) {
        if (err.response?.status === 404) {
          // Draft no longer exists on backend — purge stale IndexedDB entry (E011)
          console.warn(`Draft ${draft.draftId} no longer exists (404), clearing local copy`);
          await markSynced(draft.draftId);
          continue;
        }
        if (err.response?.status === 409) {
          // Draft was submitted — server content is now locked, local copy is obsolete
          console.warn(`Draft ${draft.draftId} already submitted (409), clearing local copy`);
          await markSynced(draft.draftId);
          continue;
        }
        console.error(`Failed to sync draft ${draft.draftId}:`, err);
        syncStatus = 'error';
        notifyListeners();
        return;
      }
    }
    syncStatus = 'synced';
    notifyListeners();
  } finally {
    syncInProgress = false;
  }
};

export const initSync = () => {
  if (isDemoUser()) return { skipped: true, reason: 'demo' };
  // Sync on load if online
  if (navigator.onLine) {
    syncNow();
  }

  // Sync when coming back online
  window.addEventListener('online', () => {
    syncNow();
  });
};
